import Promise from 'bluebird';
import { get } from 'lodash';
import { httpClient } from '../../lib/utils';
import { SAVE_LEVEL } from '../constants/Level';
import store from '../store/Store';
import { Service } from './Service';
import { envValue } from '../../config/envUtils';

const SOURCEURL = envValue('SOURCEURL');
const SBEM_CLEAR_CACHE_URL = envValue('SBEM_CLEAR_CACHE_URL');

export function clearSbemLabelsCache(chainId, hotelId, language, saveLevel = SAVE_LEVEL.HOTEL) {
    return httpClient.post(SBEM_CLEAR_CACHE_URL, { chainId, hotelId, language, saveLevel, keyType: 'labels' });
}

function getSystemLabels(code) {
    return httpClient
        .get(`${SOURCEURL}/shs-bedesigner-services/v1/contents/labels/systemdefault?contentcode=${code}`)
        .then(response => {
            const resultCode = get(response, 'data.Result.ResultCode');
            const value = get(response, 'data.BEDesignerResult.Value.Data');
            if (resultCode === 'SUCCESS') {
                return Promise.resolve({
                    ContentCode: code,
                    Value: value
                });
            } else {
                return Promise.reject('Cannot retrieve labels at this time');
            }
        });
}

function getFoldedLabels(chainId, hotelId, explicitLevel = SAVE_LEVEL.HOTEL, foldlevel = SAVE_LEVEL.HOTEL, code) {
    return httpClient
        .get(
            `${SOURCEURL}/shs-bedesigner-services/v1/contents/labels?${Service.paramProxy(
                chainId,
                hotelId,
                null,
                explicitLevel
            )}&contentcode=${code}&foldlevel=${foldlevel}`
        )
        .then(foldedResponse => {
            const resultCode = get(foldedResponse, 'data.Result.ResultCode');
            const value = get(foldedResponse, 'data.BEDesignerResult.Value');

            if (resultCode === 'SUCCESS') {
                return Promise.resolve({
                    ContentCode: code,
                    Value: value.Folded
                });
            } else {
                return Promise.reject('Cannot retrieve labels at this time');
            }
        });
}

function higherOrder(level) {
    if (level === SAVE_LEVEL.HOTEL) return SAVE_LEVEL.BRAND;
    if (level === SAVE_LEVEL.BRAND) return SAVE_LEVEL.CHAIN;
    if (level === SAVE_LEVEL.CHAIN) return SAVE_LEVEL.SYSTEM;
    return null;
}

export class BatchLabelsService {
    static getExplicit(chainId, hotelId, level = SAVE_LEVEL.HOTEL, languageCodes = []) {
        if (languageCodes.length === 0) {
            return Promise.resolve({});
        }

        let result;
        if (level === SAVE_LEVEL.SYSTEM) {
            result = Promise.all(languageCodes.map(getSystemLabels));
        } else {
            result = httpClient
                .get(
                    `${SOURCEURL}/shs-bedesigner-services/v1/contents/labels/languages?${Service.paramProxy(
                        chainId,
                        hotelId,
                        null,
                        level
                    )}&contentcodes=${encodeURI(languageCodes.join(','))}&explicitlevel=${level}`
                )
                .then(response => {
                    const resultCode = get(response, 'data.Result.ResultCode', null);
                    const value = get(response, 'data.BEDesignerResult.LabelsList', []);
                    if (resultCode === 'SUCCESS') {
                        return Promise.resolve(
                            value.map(labelSet => ({
                                ContentCode: labelSet.ContentCode,
                                Value: labelSet.Value.Explicit.Data
                            }))
                        );
                    } else {
                        return Promise.reject('Cannot retrieve labels at this time');
                    }
                });
        }
        return result.then(labels =>
            labels.reduce(
                (labelsResult, labelSet) =>
                    Object.assign(labelsResult, {
                        [labelSet.ContentCode]: labelSet.Value
                    }),
                {}
            )
        );
    }

    static getFolded(chainId, hotelId, level = SAVE_LEVEL.HOTEL, languageCodes = []) {
        if (languageCodes.length === 0) {
            return Promise.resolve({});
        }
        let result;
        if (level === SAVE_LEVEL.SYSTEM) {
            result = Promise.all(languageCodes.map(getSystemLabels));
        } else {
            result = Promise.all(languageCodes.map(code => getFoldedLabels(chainId, hotelId, level, level, code)));
        }
        return result.then(labels =>
            labels.reduce(
                (labelsResult, labelSet) =>
                    Object.assign(labelsResult, {
                        [labelSet.ContentCode]: labelSet.Value
                    }),
                {}
            )
        );
    }

    static getHigherOrder(chainId, hotelId, level = SAVE_LEVEL.HOTEL, languageCodes = []) {
        const higherOrderLevel = higherOrder(level);
        if (higherOrderLevel !== null) {
            return BatchLabelsService.getFolded(chainId, hotelId, higherOrderLevel, languageCodes);
        } else {
            return Promise.reject('Invalid labels level');
        }
    }

    static save(chainId, hotelId, level = SAVE_LEVEL.HOTEL, labels) {
        const payload = [];
        Object.entries(labels).forEach(entry => {
            payload.push({
                ContentCode: entry[0],
                Data: entry[1],
                MetaData: {}
            });
        });
        return httpClient.put(
            `${SOURCEURL}/shs-bedesigner-services/v1/contents/labels/languages?${Service.paramProxy(
                chainId,
                hotelId,
                null,
                level
            )}`,
            payload
        );
    }
}

export default BatchLabelsService;



// WEBPACK FOOTER //
// ./src/services/BatchLabelsService.js